import { GeneralSettingResponse, NewsletterFooterStrip } from "@/api/generated/BMWInterfaces"
import NewsletterSection from "@/components/Newsletter/NewsletterSection"
import DelekMotorsLogo from "@/components/shared/icons/DelekMotorsLogo"
import DirectFooterIcon from "@/components/shared/icons/DirectFooterIcon"
import MintNioIcon from "@/components/shared/icons/MintNioIcon"
import { HomePageContainer } from "@/components/shared/shared"
import LinkWithParams from "@/lib/common/LinkWithParams"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { FooterColumns, FooterColumnType } from "@/lib/common/SiteLinks"
import { footerButtonClick } from "@/lib/gtm"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"

const CMS_LINK_TITLES = {
  "מדיניות הפרטיות": { key: "PolicyLink", blank: true },
  "תנאי שימוש": { key: "TermsOfUse", blank: false },
  "תקנון הרכישה": { key: "TermsOfPurchase", blank: false },
  "תקנון ימי מכירות": { key: "sales_terms", blank: false },
} as const

const StyledWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${colors.directBlack};
  position: relative;
  overflow: hidden;
  max-width: 100vw;
`

const StyledBackgroundLayer = styled.div<{ $bottom?: number }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 360px;
  height: 272px;
  bottom: ${(p) => p.$bottom || "0"}px;
  top: unset;
  left: 0;
  right: unset;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 751.211px;
    height: 368.209px;
    left: -39px;
    bottom: -60px;
  }
  ${(p) => p.theme.breakpoints.up("lg")} {
    left: 81px;
  }
`

const StyledFooterContainer = styled(HomePageContainer, {
  shouldForwardProp: (prop) => prop !== "$isHomePage",
})<{ $isHomePage: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 32px 32px 55px;
  gap: 32px;
  position: relative;
  z-index: 2;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 24px;
    padding: ${({ $isHomePage }) => ($isHomePage ? "32px" : "40px")} 88px 40px;
    max-width: calc(1280px + 88px + 88px);
  }
  ${(p) => p.theme.breakpoints.up("lg")} {
    padding: ${({ $isHomePage }) => ($isHomePage ? "32px" : "40px")} 88px 40px;
    max-width: calc(1440px + 88px + 88px);
  }
`

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const DirectLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 14px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 15.98px 0;
  }
`

const ColumnsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
`

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  row-gap: 32px;
  width: 100%;
  justify-content: space-between;
  padding-inline-end: 10px;
`

const LinkColumn = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: right;
`

const ColumnHeader = styled(Typography)`
  color: ${colors.white};
  font-weight: 700;
  line-height: 24px;
  text-align: right;
  letter-spacing: unset;
  padding: 1px 0;
`

const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;
`

const StyledLinkText = styled(Typography)`
  color: ${colors.white};
  line-height: 24px;
  text-align: right;
  letter-spacing: unset;
  padding: 1px 0;
`

const DelekLogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const StyledMintStrip = styled.div<{ paddingBottom?: number }>`
  width: 100%;
  background: ${colors.white};
  padding: 16px 32px;
  ${(p) => p.theme.breakpoints.down("md")} {
    padding-bottom: ${(p) => (p.paddingBottom || 0) + 16}px;
  }
  display: flex;
  justify-content: center;
  gap: 8px;
  ${(p) => p.theme.breakpoints.up("md")} {
    position: relative;
    z-index: 2;
  }
`

const StyledLogoMintContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
`

const Footer = ({
  settings,
  newsletter,
}: {
  settings: GeneralSettingResponse[]
  newsletter: NewsletterFooterStrip | null
}): React.ReactElement => {
  const pathname = usePathname()
  const isHomePage = pathname === "/"
  const isCarPage = pathname.includes("/car")
  const isSearchPage = pathname.includes("/search")
  const isBenefitPage = pathname.includes("/benefit")
  const mintPadding = isHomePage
    ? 72
    : isCarPage
      ? 107
      : isSearchPage || isBenefitPage
        ? 56
        : undefined

  const getSettingValue = (key: string): string => {
    return settings.find((setting) => setting.key === key)?.value || "#"
  }

  const columns: FooterColumnType[] = FooterColumns.map((column) => ({
    ...column,
    links: column.links
      .map((link) => {
        const cms = CMS_LINK_TITLES[link.title as keyof typeof CMS_LINK_TITLES]
        if (!cms) return link
        return { ...link, link: getSettingValue(cms.key), blank: cms.blank }
      })
      .filter((link) => link.link !== "#"),
  }))

  const renderColumn = (column: FooterColumnType, key: number): JSX.Element => (
    <LinkColumn key={key} aria-label={column.header}>
      <ColumnHeader variant="body1">{column.header}</ColumnHeader>
      <LinkList>
        {column.links.map((el, j) => (
          <li key={j}>
            <LinkWithParams
              href={el.link}
              style={{ textDecoration: "none" }}
              target={el.blank ? "_blank" : undefined}
              onClick={() => {
                footerButtonClick(el.title)
              }}
            >
              <StyledLinkText variant="body2">{el.title}</StyledLinkText>
            </LinkWithParams>
          </li>
        ))}
      </LinkList>
    </LinkColumn>
  )

  return (
    <>
      <NewsletterSection content={newsletter} />
      <StyledWrapper>
        <Mobile>
          <StyledBackgroundLayer $bottom={mintPadding || 0}>
            <Image
              src="/images/footerBackgroundMobile.png"
              alt="footer background"
              fill
              style={{ objectFit: "contain" }}
            />
          </StyledBackgroundLayer>
        </Mobile>
        <Desktop>
          <StyledBackgroundLayer>
            <Image
              src="/images/footerBackgroundDesktop.png"
              alt="footer background"
              fill
              style={{ objectFit: "contain" }}
            />
          </StyledBackgroundLayer>
        </Desktop>
        <StyledFooterContainer $isHomePage={isHomePage}>
          <LinksSection>
            <DirectLogoWrapper>
              <Desktop>
                <DirectFooterIcon />
              </Desktop>
              <Mobile>
                <DirectFooterIcon />
              </Mobile>
            </DirectLogoWrapper>
            <Desktop sx={{ width: "100%" }}>
              <ColumnsRow>{columns.map((column, i) => renderColumn(column, i))}</ColumnsRow>
            </Desktop>
            <Mobile sx={{ width: "100%" }}>
              <ColumnsGrid>{columns.map((column, i) => renderColumn(column, i))}</ColumnsGrid>
            </Mobile>
          </LinksSection>

          <DelekLogoRow>
            <DelekMotorsLogo />
          </DelekLogoRow>
        </StyledFooterContainer>

        <StyledMintStrip paddingBottom={mintPadding}>
          <Link href="https://mintapp.co.il" target="_blank">
            <StyledLogoMintContainer>
              <MintNioIcon />
            </StyledLogoMintContainer>
          </Link>
        </StyledMintStrip>
      </StyledWrapper>
    </>
  )
}

export default Footer
